import './_Thanks.scss'
import { useEffect, useMemo, useState, useRef } from 'react'
import { useAnnualPlan, usePricing } from '../../hooks/featureFlags'
import Modal from './Modal'
import { usePostHog } from 'posthog-js/react'
import { useDispatch, useSelector } from 'react-redux'
import { useLocalStorage } from '../../helpers/utility'
import { TbConfetti } from 'react-icons/tb'
import Button from '../Buttons'
import { selectUser, setUser } from '../../redux/systemSlice'
import { USE_CASE } from '../../redux/types'
import { useAuth } from '../../contexts/Auth'
import { IoBook } from 'react-icons/io5'
import { FaGraduationCap } from 'react-icons/fa6'
import { PiBooksFill } from 'react-icons/pi'
import { FaBriefcase } from 'react-icons/fa'
import { logGoogleAdsEvent } from '../../helpers/analytics'

const useCaseDisplay = {
	[USE_CASE.PRE_UNIVERSITY]: 'Pre-University',
	[USE_CASE.UNDERGRADUATE]: 'Undergraduate',
	[USE_CASE.GRADUATE]: 'Graduate',
	[USE_CASE.PROFESSIONAL]: 'Professional',
}

const useCaseIcons = {
	[USE_CASE.PRE_UNIVERSITY]: IoBook({ size: 16 }),
	[USE_CASE.UNDERGRADUATE]: FaGraduationCap({ size: 18 }),
	[USE_CASE.GRADUATE]: PiBooksFill({ size: 18 }),
	[USE_CASE.PROFESSIONAL]: FaBriefcase({ size: 16 }),
}

export const ThanksContent = ({ setThanksModalOpen }: { setThanksModalOpen: (open: boolean) => void }) => {
	const user = useSelector(selectUser)
	const { currentUser } = useAuth() as any
	const [useCase, setUseCase] = useState<USE_CASE | undefined>(user.useCase)

	const updateUserRole = async () => {
		const requestOptions = {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				authorization: currentUser ? `Bearer ${await currentUser.getIdToken()}` : '',
			},
			body: JSON.stringify({ user_id: user.id, useCase: useCase }),
		}
		fetch(process['env']['REACT_APP_API_ROOT'] + '/user/update/', requestOptions)
	}

	return (
		<div>
			<div className={`flex flex-col gap-2 items-center bg-secondary pt-5 pb-5 px-3`}>
				<div className="w-12 h-12 bg-white rounded-full flex items-center justify-center">
					{TbConfetti({ className: 'text-2xl' })}
				</div>
				<div className={`text-2xl text-center font-semibold`}>
					{user.isOnFreeTrial ? 'Your free trial has started!' : "You're now a Premium member!"}
				</div>
				<div className="text-center text-sm">Help us customize your writing experience</div>
			</div>
			<div className="flex flex-col gap-4 py-5 md:px-12 px-5">
				<div>Which role best describes you?</div>
				{Object.values(USE_CASE).map((role) => (
					<div
						key={role}
						className={`py-2 border border-gray-900 px-4 flex items-center cursor-pointer gap-3 rounded font-medium ${
							useCase === role ? 'bg-secondary hover:bg-[#a1edeb]' : 'hover:bg-gray-100'
						}`}
						onClick={() => {
							setUseCase(role)
						}}
					>
						{useCaseIcons[role]}
						{useCaseDisplay[role]}
					</div>
				))}
				<div className="grid md:grid-cols-3 grid-cols-2 gap-2 mt-2">
					<div>
						<Button
							type="ghost"
							className="text-sm font-normal text-gray-700 text-left"
							onClick={() => setThanksModalOpen(false)}
						>
							Skip for now
						</Button>
					</div>
					<div className="md:text-center text-right">
						<Button
							type="default"
							disabled={!useCase}
							onClick={() => {
								updateUserRole()
								setThanksModalOpen(false)
							}}
						>
							Continue
						</Button>
					</div>
				</div>
			</div>
		</div>
	)
}

function Thanks({
	thanksModalOpen,
	setThanksModalOpen,
}: {
	thanksModalOpen: boolean
	setThanksModalOpen: (open: boolean) => void
}) {
	const user = useSelector(selectUser)
	const { currentUser } = useAuth() as any
	const dispatch = useDispatch()
	const intervalRef = useRef<NodeJS.Timeout | null>(null)

	// Function to fetch user info similar to getUserInfo in Auth.tsx
	const fetchUserInfo = async () => {
		if (!currentUser) return

		const requestOptions = {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				authorization: `Bearer ${await currentUser.getIdToken()}`,
			},
			body: JSON.stringify({
				uid: currentUser?.uid,
				email: currentUser?.providerData?.[0]?.email ?? '',
			}),
		}

		try {
			const res = await fetch(process['env']['REACT_APP_API_ROOT'] + '/user/retrieve/', requestOptions)
			const result = await res.json()
			if (result) {
				// Update user in Redux store
				dispatch(setUser(result))
			}
		} catch (error) {
			console.error('Error fetching user info:', error)
		}
	}

	// Effect to poll for user info when modal is open and planId is null
	useEffect(() => {
		if (thanksModalOpen && (!user.planId || user.planId === null)) {
			// Start polling every 2 seconds
			intervalRef.current = setInterval(() => {
				fetchUserInfo()
			}, 2000)

			// Also fetch immediately
			fetchUserInfo()
		} else if (intervalRef.current) {
			// Stop polling if modal is closed or planId is not null
			clearInterval(intervalRef.current)
			intervalRef.current = null
		}

		// Cleanup on unmount or dependency change
		return () => {
			if (intervalRef.current) {
				clearInterval(intervalRef.current)
				intervalRef.current = null
			}
		}
	}, [thanksModalOpen, user.planId, currentUser])

	return (
		<Modal
			open={thanksModalOpen}
			closeModal={() => {
				setThanksModalOpen(false)
			}}
			portalClassName="thanks"
			omitClose={true}
		>
			<ThanksContent setThanksModalOpen={setThanksModalOpen} />
		</Modal>
	)
}

export default Thanks
